import React, { useContext, useState, useEffect } from "react";
import { matchPath } from "react-router";
import { useLocation, Redirect, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import KeyButton from "./KeyButton";
import { setSelectedRoom, setSelectedLanding } from "../actions/hotel";
import { useTranslation } from "react-i18next";
import pkg from "../../package.json";
import { postMessageToParent } from "./Design/DesignUtils";
import { getUser, setOnBoardingUI } from "../actions/user";
import { getIsEmbedded } from "../actions/config";
import { MessagesContext } from "contexts/messages";
import { ProjectContext } from "contexts/project";
import { NotificationContext, NOTIF_KIND_CHECKIN } from "contexts/notifications";
import useOnBoardingData from "hooks/useOnBoardingData";
import { getPropertyLandingState } from "../utils/landing";

const Tabs = (props) => {
    const { t } = useTranslation();
    const location = useLocation();

    const { room, project } = useContext(ProjectContext);
    const { notifications } = useContext(NotificationContext);
    const { hasUnread } = useContext(MessagesContext);

    const hasRegisteredUsers = project?.permissionsConfig?.registeredUsers === true;
    const hasMessaging = project?.permissionsConfig?.messaging === true;
    const hasPrecheckin = project?.permissionsConfig?.preCheckIn === true;

    const shouldRenderTabs = hasPrecheckin || hasMessaging || hasRegisteredUsers;
    const selectedLanding = useSelector((state) => state.session?.selectedLandings?.[project?.ref]);
    const userLogged = useSelector(getUser);
    const isEmbedded = useSelector(getIsEmbedded);
    const { obData, showObDialog, showObText, showNotif, hideLoginDialog } = useOnBoardingData();
    const history = useHistory();

    const hasUnreadMessage = hasUnread();
    const hasUnreadNotif = notifications?.filter((n) => !n.isRead).length > 0;
    const hasCheckInPending = notifications?.filter((n) => !n.isRead && n.kind === NOTIF_KIND_CHECKIN).length > 0;
    const isNotSignInPage = !location.pathname.includes("signin");

    const showDialogInTab = {
        user: {
            value: isNotSignInPage && (showObDialog || (userLogged === null && !hideLoginDialog)),
            text: showObText || (userLogged === null ? t("sign-in-and-enjoy-all-our-services") : ""),
        },
    };

    const isPath = (path, exact) => {
        return matchPath(location.pathname, {
            path: path,
            exact: exact,
        });
    };

    const dispatch = useDispatch();
    const [redirectTo, setRedirectTo] = useState("");

    const hotelsScreen = "/site/list";
    const hotelScreen = "/site";
    const roomScreen = "/site/room";

    const goToHotel = () => {
        const propertyLanding = getPropertyLandingState(selectedLanding);
        const shouldGoToPropertyLanding =
            propertyLanding &&
            (selectedLanding?.history?.length > 0 || selectedLanding?.landingRef !== propertyLanding.landingRef);

        if (isPath(roomScreen, false) && room?.number !== null) {
            dispatch(setSelectedRoom(null));
        }
        if (!isPath(hotelScreen, true) && project?.name) {
            history.push(hotelScreen);
            if (shouldGoToPropertyLanding) {
                dispatch(setSelectedLanding(propertyLanding));
            }
        } else {
            if (room?.number) {
                let goToHotelList = true;
                let selectedRoom = room?.number;
                if (shouldGoToPropertyLanding) {
                    goToHotelList = false;
                    dispatch(setSelectedLanding(propertyLanding));
                    dispatch(setSelectedRoom(null));
                    setTimeout(() => {
                        dispatch(setSelectedRoom(selectedRoom));
                    }, 500);
                }
                if (goToHotelList) {
                    if (isEmbedded) {
                        postMessageToParent({ action: "exit" });
                        return;
                    } else {
                        setRedirectTo(hotelsScreen);
                    }
                }
            } else {
                setRedirectTo(hotelsScreen);
            }
        }
    };

    const goToProfile = () => {
        if (isEmbedded) {
            postMessageToParent({
                action: "ensureUser",
                params: {
                    page: "/profile",
                },
            });
        } else {
            if (!isPath("/profile", true)) {
                setRedirectTo("/profile");
            }
        }
    };

    const goToStays = () => {
        if (!isPath("/stays", true)) {
            setRedirectTo("/stays");
        }
    };

    const goToNotifications = () => {
        if (!isPath("/notifications", true)) {
            setRedirectTo("/notifications");
        }
    };

    if (redirectTo && !isPath(redirectTo, true)) {
        return <Redirect push to={redirectTo} />;
    }

    if (!shouldRenderTabs) {
        return null;
    }

    const buttons = [
        {
            id: "tab-property",
            name: t("hotel"),
            icon: "hotel",
            action: () => goToHotel(),
            active: isPath(hotelScreen, false),
            notif: false,
        },
        hasPrecheckin && {
            id: "tab-stay",
            name: t("stays"),
            icon: "stay",
            action: () => goToStays(),
            active: isPath("/stays", false),
            notif: hasCheckInPending,
        },
        hasPrecheckin
            ? {
                  id: "tab-notif",
                  name: t("notifications"),
                  icon: "notif",
                  action: () => goToNotifications(),
                  active: isPath("/notifications", false),
                  notif: hasUnreadNotif || hasUnreadMessage,
              }
            : null,
        hasRegisteredUsers && {
            id: "tab-user",
            name: userLogged ? t("profile") : t("signin"),
            icon: "user",
            action: () => goToProfile(),
            active: isPath("/profile", false),
            notif: obData || showNotif || !userLogged,
            dialog: showDialogInTab.user?.value === true,
            dialogContent: showDialogInTab.user?.text || "",
            dialogAction: () => {
                if (showObDialog) {
                    dispatch(
                        setOnBoardingUI({
                            showDialog: false,
                            hideLoginDialog: true,
                        })
                    );
                }
                if (!userLogged) {
                    dispatch(
                        setOnBoardingUI({
                            hideLoginDialog: true,
                        })
                    );
                }
            },
        },
    ];
    const visibleButtons = buttons.filter(Boolean);

    return (
        <>
            <div className="tab-bar">
                {props.children ? (
                    props.children
                ) : (
                    <nav>
                        {visibleButtons.map((b, i) => (
                            <div key={"tab-k-" + i} style={{ position: "relative", display: "inline-block" }}>
                                <button id={b.id} onClick={b.action} className={b.active ? "text-accent" : ""}>
                                    <div className="notif-icon" style={{ position: "relative" }}>
                                        {b.notif ? <div className="circle"></div> : ""}
                                        <i className={"icon icon-" + b.icon + (b.active ? "-solid" : "")}></i>
                                    </div>
                                    <div>{b.name}</div>
                                </button>

                                {b.dialog ? (
                                    <button
                                        id={"tab-dialog-" + i}
                                        onClick={b.dialogAction}
                                        className="message-dialog float-animation"
                                    >
                                        {b.dialogContent}
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </nav>
                )}
            </div>
            {props.children ? null : props.hideKey === true ? null : <KeyButton></KeyButton>}
        </>
    );
};

export default Tabs;
